'use client';

import { TableQueryParams } from '@/components/custom-table-ag';
import { FilterZodSchema } from '@/server/base/base-trpc-service';
import { SortValue } from '@/types/enum';
import deepmerge from 'deepmerge';
import {
    Dispatch,
    MutableRefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useURLState } from './use-url-state';

/**
 * Các key tương ứng với các tham số trên URL
 */
const ObjectKeyParams = {
    /**
     * Số trang
     */
    pageNumber: 'page',
    /**
     * Kích thước item của trang
     */
    pageSize: 'psize',
    /**
     * search keyword
     */
    search: 's',
    /**
     * sort by gi do
     */
    sort: 'sort',
    /**
     *Extend Info
     */
    extends: 'ext',
};

/**
 *
 * @param initalValues Giá trị khởi tạo cho biến pageInfo
 * @param isGetParamFromUrl lấy dữ liệu từ param set vào pageInfo khi khởi tạo
 * @param isSync đồng bộ dữ liệu lên params khi pageInfo thay đổi
 * @param onSearch Hàm custom biến keyword search thì FilterBuilders
 * @param initExtendFilter Giá trị khởi tạo cho biến extendFilter
 * @returns {Object} Object state, setState
 * @property {State} pageInfo - pageInfo của table
 * @property {State} rowSelected - Các rows được chọn trên table
 * @property {State} resizeTableStatus - Trạng thái resize của table
 * @property {string} searchValue - keyword search
 * @property {SetState} setPageInfo - setPageInfo
 * @property {SetState} setRowSelected - setRowSelected
 * @property {SetState} setResizeTableStatus - setResizeTableStatus
 * @property {Function} onChangeSearch - Hàm search custom
 */
export const useTablePageInfo = <T extends {} = any, E extends {} = any>(
    initalValues: TableQueryParams,
    isGetParamFromUrl: boolean,
    isSync: boolean,
    onSearch?: (keyword: string, extendFilters?: E) => FilterZodSchema[],
    initExtendFilter?: E
) => {
    const [isInited, setIsInited] = useState(false);

    const [pageSize, updatePageSize] = useURLState(ObjectKeyParams.pageSize);
    const [pageNumber, updatePageNumber] = useURLState(
        ObjectKeyParams.pageNumber
    );
    const [sorts, updateSorts] = useURLState(ObjectKeyParams.sort);
    const [searchValue, updateSearchValue] = useURLState(
        ObjectKeyParams.search
    ); // keysearch on params
    const [extFilterOnUrl, updateExtFilterOnUrl] = useURLState(
        ObjectKeyParams.extends
    ); // keysearch on params

    const [pageInfo, setPageInfo] = useState<TableQueryParams>(initalValues);
    const [extendFilter, setExtendFilter] = useState<E | undefined>(
        initExtendFilter
    );
    const [rowSelected, setRowSelected] = useState<T[]>([]);
    const [resizeTableStatus, setResizeTableStatus] = useState<boolean>(
        typeof window !== 'undefined' ? window?.innerWidth <= 1024 : true
    );

    /**Lưu trữ các key value filter */
    const [objKeyValueFilter, setObjKeyValueFilter] = useState<
        Record<string, any[]>
    >(() => mapFilterToObjectKeyValue(initalValues.filters, initExtendFilter));

    /**
     * TODO:
     * sử dụng ref để lưu lại key của những trường trả về từ hàm searhc
     */
    const _searchRef = useRef<Record<string, boolean>>({});

    // Lấy giá trị từ params set vào state
    useEffect(() => {
        if (isGetParamFromUrl) {
            let hvParamFormUrl = false;
            let _pageInfo = { ...initalValues };

            const extFilter = initExtendFilter
                ? convertExtFilterFromURL(extFilterOnUrl, initExtendFilter)
                : undefined;

            const hvSearch =
                (searchValue.length !== 0 || !!extFilter) &&
                typeof onSearch === 'function';

            let _filters: FilterZodSchema[] = collectNecessaryFilters(
                initalValues.filters ?? [],
                hvSearch ? onSearch(searchValue, extFilter as E) : [],
                _searchRef
            );

            /**
             * Ở đây dính 1 case nếu trên params có data thì bị set state và reload lại 1 lần
             * Nên gán thêm trường hợp kiểm tra nếu không có change URL thì ko set state
             */

            if (!isNaN(parseInt(pageSize))) {
                _pageInfo.pageSize = parseInt(pageSize);
                hvParamFormUrl = true;
            }

            if (!isNaN(parseInt(pageNumber))) {
                _pageInfo.pageNumber = parseInt(pageNumber);
                hvParamFormUrl = true;
            }

            if (!!sorts) {
                const mappedSort = convertSortFromURL(sorts);
                if (mappedSort?.length) {
                    _pageInfo.sorts = [...mappedSort];
                    hvParamFormUrl = true;
                }
            }

            if (_filters.length > 0 || !!extFilterOnUrl) {
                hvParamFormUrl = true;
            }

            if (hvParamFormUrl) {
                setPageInfo({
                    ..._pageInfo,
                    filters: _filters,
                });
                setObjKeyValueFilter(mapFilterToObjectKeyValue(_filters));
                extFilter && setExtendFilter(extFilter as E);
                // console.trace("Updating state with useEffect 'pageInfo'");
            }
        }

        setIsInited(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**hàm setPageInfo custom */
    const onChangePageInfo: Dispatch<SetStateAction<TableQueryParams>> =
        useCallback(
            (fn) => {
                setPageInfo((p) => {
                    let pInfo: TableQueryParams;
                    if (typeof fn === 'function') {
                        pInfo = fn(p);
                    } else {
                        pInfo = { ...fn };
                    }

                    if (isSync) {
                        const pSize = pInfo.pageSize?.toString();
                        const pNumber = pInfo.pageNumber?.toString();
                        const sorts = pInfo.sorts;
                        if (pSize && pSize !== pageSize) {
                            updatePageSize(pSize);
                        }
                        if (pNumber && pNumber !== pageNumber) {
                            updatePageNumber(pNumber);
                        }
                        if (sorts) {
                            updateSorts(mapSortForURL(sorts));
                        }
                    }

                    return pInfo;
                });
                // console.trace("Updating state 'pageInfo'");
            },
            [
                isSync,
                pageNumber,
                pageSize,
                updatePageNumber,
                updatePageSize,
                updateSorts,
            ]
        );

    /**Hàm search custom */
    const onChangeSearch = useCallback(
        (fn: SetStateAction<string | undefined>, extFilter?: E) => {
            let search: string;
            if (typeof fn === 'function') {
                search = fn(searchValue) ?? '';
            } else {
                search = fn ?? '';
            }

            setPageInfo((state) => {
                if (isSync) {
                    if (search !== searchValue) {
                        updateSearchValue(search);
                    }
                    if (state.pageNumber !== 1) {
                        updatePageNumber('1');
                    }
                    if (state.sorts) {
                        updateSorts(mapSortForURL(state.sorts));
                    }
                }

                let _filters = collectNecessaryFilters(
                    [...(state.filters ?? [])],
                    onSearch?.(search, extFilter) ?? [],
                    _searchRef
                );

                setExtendFilter(extFilter);
                setObjKeyValueFilter(
                    mapFilterToObjectKeyValue(_filters, extFilter)
                );

                return {
                    ...state,
                    pageNumber: 1,
                    // filters: [...onSearch(search)],
                    filters: _filters,
                };
            });

            extFilter && updateExtFilterOnUrl(mapExtFilterForURL(extFilter));
            // console.trace("Updating state 'pageInfo'");
        },
        [
            updateExtFilterOnUrl,
            searchValue,
            isSync,
            onSearch,
            updateSearchValue,
            updatePageNumber,
            updateSorts,
        ]
    );

    return {
        /**
         * Đã khởi tạo xong thông tin liên quan đến table
         * Một số trường hợp nêu sử dụng trường này để biết khi nào cần gọi API
         */
        isInited,

        // state
        pageInfo,
        rowSelected,
        resizeTableStatus,
        searchValue,
        /**State extendd filter */
        extendFilter,

        // set state
        setPageInfo: onChangePageInfo,
        setRowSelected,
        setResizeTableStatus,

        // change search
        onChangeSearch,

        // object keyuvalue
        objKeyValueFilter,
    };
};

/**Lọc, bổ sung hoặc loại trừ các filters cũ, không cần thiết */
function collectNecessaryFilters(
    /**
     * filter hiện tại
     */
    currentFilters: FilterZodSchema[],
    /**filter mới sẽ update */
    newFilters: FilterZodSchema[],
    /** */
    keySearchRef: MutableRefObject<Record<string, boolean>>
): FilterZodSchema[] {
    let _fill: FilterZodSchema[] = [...currentFilters];

    function _loop(loopData: FilterZodSchema[]) {
        const extFilters: FilterZodSchema[] = [];

        for (let nf of loopData) {
            if (nf.key) {
                if (nf.filters && nf.filters?.length) {
                    nf.filters = _loop(nf.filters);
                }

                if (keySearchRef.current[nf.key]) {
                    const _idx = extFilters.findIndex((x) => x.key === nf.key);
                    if (_idx !== -1) {
                        extFilters[_idx] = { ...nf };
                    } else {
                        extFilters.push(nf);
                    }
                } else {
                    // Đánh dấu key này dùng để search và chưa tồn tại trong mảng filter
                    // ==> push vào mảng
                    keySearchRef.current[nf.key] = true;
                    extFilters.push(nf);
                }
            }
        }
        return extFilters;
    }

    /**
     * TODO:
     * Hàm này chưa tối ưu do sử dụng tới 2 loop lòng nhau, và phải sử dụng useRef để lưu lại keysearch
     */
    if (newFilters.length > 0) {
        _fill = _loop(newFilters);
    } else {
        _fill = _fill.filter((x) => {
            /**
             * Nếu filters search trả về mảng rỗng
             * ==> Có nghĩa là đã clear đi các search cũ
             */
            const hasKeyInFilters =
                x.key && keySearchRef.current[x.key] ? true : false;
            return !hasKeyInFilters;
        });
    }

    return _fill;
}

/**
 * Nôm na là hàm này kiểm tra các giá trị trong trường filters có bị lặp lại không
 */
function mapFilterToObjectKeyValue(
    filters?: FilterZodSchema[],
    extendFilter?: Record<string, any>
) {
    let obj: Record<string, any[]> = {};

    function _loop(loopDatas: FilterZodSchema[]) {
        if (loopDatas?.length) {
            for (let x of loopDatas) {
                if (x.key && x.values) {
                    obj[x.key] = x.values as any[];
                } else if (x.key && x.filters?.length) {
                    _loop(x.filters);
                }
            }
        }
    }

    _loop(filters ?? []);

    if (extendFilter) {
        for (let key in extendFilter) {
            obj[key] = [extendFilter[key]];
        }
    }

    return obj;
}

/**
 * Tạo chuỗi encodeURIComponent từ object extendFilter
 * @returns
 */
function mapExtFilterForURL(extendFilter: Record<string, any>) {
    try {
        let obj: Record<string, any> = {};
        if (extendFilter) {
            for (let key in extendFilter) {
                if (
                    typeof extendFilter[key] !== 'undefined' &&
                    extendFilter[key] !== null &&
                    extendFilter[key] !== ''
                ) {
                    obj[key] = extendFilter[key];
                }
            }
        }

        return encodeURIComponent(JSON.stringify(obj));
    } catch (error) {
        console.error(`Error when map extends filter for URL`, error);
        return '';
    }
}

function convertExtFilterFromURL(
    extFilterOnUrl: string,
    initData: Record<string, any>
) {
    try {
        if (extFilterOnUrl && extFilterOnUrl.length > 0) {
            const str = decodeURIComponent(extFilterOnUrl);
            const obj = JSON.parse(str);

            if (typeof obj === 'object' && obj !== null) {
                for (const key in obj) {
                    if (!Object.prototype.hasOwnProperty.call(initData, key)) {
                        delete obj[key];
                    }
                }

                return deepmerge(initData, obj) as typeof initData;
            }
            return undefined;
        }
    } catch (error) {
        console.error(`Error when convert extend filter from URL`, error);
    }
}

function mapSortForURL(sorts: { key: string; value: any }[]) {
    try {
        let vals: string[] = [];
        if (sorts && Array.isArray(sorts) && sorts.length > 0) {
            for (let s of sorts) {
                if (s.key && typeof s.value === 'number') {
                    vals.push(`${s.key}:${s.value}`);
                }
            }
        }
        const dta = encodeURIComponent(vals.length ? vals.join(',') : '');
        return dta;
    } catch (error) {
        console.error(`Error when map sort for URL`, error);
        return '';
    }
}

function convertSortFromURL(sortStr?: string) {
    try {
        if (!sortStr) return;
        const splitData = decodeURIComponent(sortStr).split(',');

        const sorts: { key: string; value: any }[] = [];

        if (splitData.length > 0) {
            for (let dta of splitData) {
                const [key, value] = dta.split(':');
                const parseValue = parseInt(value);
                const isValidSortValue =
                    parseValue === SortValue.ASC ||
                    parseValue === SortValue.DESC;

                if (key && isValidSortValue) {
                    sorts.push({
                        key,
                        value: parseInt(value),
                    });
                }
            }
        }

        return sorts;
    } catch (error) {
        console.error(`Error when parse sort from URL`, error);
    }
}
